export interface CartAttribute {
    name: string;
    value: string;
}

export interface CartItem {
    idProduct: string;
    idProductVariant: string;
    name: string;
    price: number;
    quantity: number;
    image: string;
    stock?: number;
    unitMeasureCode?: number;
    unitLabel?: string;
    cantDecimal?: number;
    minimumQuantity?: number;
    attributes: CartAttribute[];
}

export interface AddCartItemInput extends Omit<CartItem, "quantity"> {
    quantity?: number;
}

export type CartMutationSource = "USER" | "SYSTEM";

export interface AddToCartOptions {
    source?: CartMutationSource;
}

export type CartOperation =
    | { type: "add"; item: AddCartItemInput; source?: CartMutationSource }
    | { type: "remove"; idProduct: string; idProductVariant?: string }
    | {
          type: "updateQuantity";
          idProduct: string;
          idProductVariant?: string;
          quantity: number;
      }
    | { type: "clear" };

export type CartConcurrencyMode = "legacy" | "locked";

export type CartReconciliationReason =
    | "BOOTSTRAP"
    | "LOGIN"
    | "REGISTER"
    | "REFRESH"
    | "LOGOUT"
    | "CART_OPEN"
    | "CART_MUTATION"
    | "CHECKOUT_ENTER"
    | "CHECKOUT_RESUME"
    | "MANUAL";

export type CartReconciliationChangeType = "PRICE_CHANGED" | "QUANTITY_ADJUSTED" | "REMOVED";

export interface CartReconciliationChange {
    type: CartReconciliationChangeType;
    productId: string;
    variantId: string;
    previousPrice?: number;
    currentPrice?: number;
    previousQuantity?: number;
    currentQuantity?: number;
    reason: string;
}

export interface CartReconciliationResult {
    items: CartItem[];
    pricingContext: string;
    changes: CartReconciliationChange[];
    changed?: boolean;
    skipped?: boolean;
}

export type CartReconciler = (items: readonly CartItem[]) => Promise<CartReconciliationResult>;

export type CartDiagnosticEventKind =
    | "STORAGE_RECOVERED"
    | "STORAGE_DISCARDED"
    | "MUTATION_FAILED"
    | "LEGACY_REPLACE_FAILED"
    | "VALIDATION_FAILED"
    | "RECONCILIATION_FAILED"
    | "RECONCILIATION_CONFLICT_EXHAUSTED";

export interface CartDiagnosticEvent {
    kind: CartDiagnosticEventKind;
    timestampUtc: string;
    error: unknown;
    context: Record<string, unknown>;
}

export type CartDiagnosticReporter = (event: CartDiagnosticEvent) => void | Promise<void>;

export interface CartStoreConfiguration {
    concurrencyMode: CartConcurrencyMode;
    diagnosticReporter?: CartDiagnosticReporter;
    reconcileCart?: CartReconciler;
}

export interface CartPersistedState {
    items: CartItem[];
    revision: number;
    updatedAt: string;
    pricingContext?: string;
    reconciledAt?: string;
}

export interface CartStorageEnvelope {
    state: CartPersistedState;
    version: 1;
}

export interface CartCommitResult {
    items: CartItem[];
    revision: number;
    changed: boolean;
}

export interface CartCanonicalCommitResult extends CartCommitResult {
    revisionMatched: boolean;
    pricingContext?: string;
    reconciledAt?: string;
}

export type CartPersistenceErrorCode =
    | "STORAGE_READ_FAILED"
    | "STORAGE_CORRUPTED"
    | "STORAGE_WRITE_FAILED"
    | "LOCK_UNAVAILABLE"
    | "LOCK_FAILED"
    | "UNSAFE_REPLACE_DISABLED";

export class CartPersistenceError extends Error {
    readonly code: CartPersistenceErrorCode;
    readonly cause?: unknown;

    constructor(code: CartPersistenceErrorCode, message: string, cause?: unknown) {
        super(message);
        this.name = "CartPersistenceError";
        this.code = code;
        this.cause = cause;
    }
}

export interface CartStore {
    items: CartItem[];
    persistedItems: CartItem[];
    revision: number;
    isOpen: boolean;
    isOverrideActive: boolean;
    isReconciling: boolean;
    lastPricingContext: string | null;
    lastReconciledAt: string | null;
    reconciliationError: string | null;
    reconciliationChanges: CartReconciliationChange[];

    /** @deprecated Usar beginCartOverride/endCartOverride antes de activar locked. */
    hydrateCartItems: (items: CartItem[]) => Promise<void>;
    beginCartOverride: (items: CartItem[]) => void;
    endCartOverride: () => void;
    applyOperations: (operations: CartOperation[]) => Promise<CartItem[]>;
    validateAndRepairCart: () => Promise<CartItem[]>;
    reconcilePrices: (reason: CartReconciliationReason) => Promise<CartReconciliationResult>;
    applyExternalSnapshot: (snapshot: CartPersistedState) => void;
    addToCart: (item: AddCartItemInput, options?: AddToCartOptions) => Promise<void>;
    removeFromCart: (idProduct: string, idProductVariant?: string) => Promise<void>;
    updateQuantity: (
        idProduct: string,
        idProductVariant: string | undefined,
        quantity: number,
    ) => Promise<void>;
    clearCart: () => Promise<void>;
    openCart: () => void;
    closeCart: () => void;
    toggleCart: () => void;
}
